/**
 * Arquivo: src/services/neonAuth.ts
 * Versão: 6.0.0
 */
const AUTH_URL=String(import.meta.env.VITE_NEON_AUTH_URL||'https://ep-wild-field-b63zk5u3.neonauth.c-2.sa-east-1.aws.neon.tech/controle_financeiro/auth').replace(/\/$/,'')
const AUTH_EVENT='cf-neon-auth-change'
export type NeonAuthUser={id:string;email?:string;name?:string;[key:string]:unknown}
export type NeonAuthSession={access_token?:string;expires_at?:number;user?:NeonAuthUser;[key:string]:unknown}
type AuthEnvelope={session:NeonAuthSession|null;user:NeonAuthUser|null}
let lastCredential:string|null=null
let cached:{token:string;expiresAt:number}|null=null
let sessionCache:{value:AuthEnvelope;expiresAt:number}|null=null
let sessionFlight:Promise<AuthEnvelope>|null=null
let tokenFlight:Promise<string>|null=null
let authGeneration=0
function invalidateAuth(){authGeneration++;cached=null;sessionCache=null;sessionFlight=null;tokenFlight=null}
export const neonAuthConfigured=Boolean(AUTH_URL)
const emit=()=>{if(typeof window!=='undefined')window.dispatchEvent(new Event(AUTH_EVENT))}
async function request(path:string,init:RequestInit={}){
 if(!AUTH_URL)throw new Error('Neon Auth não configurado. Defina VITE_NEON_AUTH_URL.')
 const res=await fetch(`${AUTH_URL}${path}`,{...init,credentials:'include',headers:{...(init.body?{'content-type':'application/json'}:{}),...(init.headers||{})}})
 const body=await res.json().catch(()=>({}))
 if(!res.ok)throw new Error(String(body?.message||body?.error?.message||body?.error||'Falha na autenticação.'))
 return body?.data??body
}
function normalize(payload:any):AuthEnvelope{
 if(!payload)return{session:null,user:null}
 const session=payload.session??payload?.data?.session??null
 const user=payload.user??session?.user??payload?.data?.user??null
 if(session?.access_token){const exp=Number(session.expires_at||0)*1000;cached={token:String(session.access_token),expiresAt:exp||Date.now()+600000}}
 return{session,user}
}
export const neonAuth={
 async signIn(email:string,password:string){invalidateAuth();const data=await request('/sign-in/email',{method:'POST',body:JSON.stringify({email:email.trim().toLowerCase(),password})});lastCredential=password;normalize(data);emit();return data},
 async getSession():Promise<AuthEnvelope>{
  if(sessionCache&&sessionCache.expiresAt>Date.now())return sessionCache.value
  if(sessionFlight)return sessionFlight
  const generation=authGeneration
  const flight=request('/get-session',{method:'GET'}).then(data=>{
   if(generation!==authGeneration)return {session:null,user:null}
   const value=normalize(data)
   if(value.session)sessionCache={value,expiresAt:Date.now()+15000}
   return value
  }).catch(()=>{if(generation===authGeneration){cached=null;sessionCache=null}return {session:null,user:null}})
  sessionFlight=flight
  try{return await flight}finally{if(sessionFlight===flight)sessionFlight=null}
 },
 async getAccessToken(){
  if(cached&&cached.expiresAt>Date.now()+30000)return cached.token
  if(tokenFlight)return tokenFlight
  const generation=authGeneration
  const flight=(async()=>{
   const current=await this.getSession()
   if(generation!==authGeneration)throw new Error('Sessão alterada. Entre novamente.')
   if(current.session?.access_token)return String(current.session.access_token)
   const data=await request('/token',{method:'GET'})
   if(generation!==authGeneration)throw new Error('Sessão alterada. Entre novamente.')
   const token=String(data?.token||'')
   if(!token)throw new Error('Sessão expirada. Entre novamente.')
   cached={token,expiresAt:Date.now()+840000}
   return token
  })()
  tokenFlight=flight
  try{return await flight}finally{if(tokenFlight===flight)tokenFlight=null}
 },
 async signOut(){invalidateAuth();try{await request('/sign-out',{method:'POST',body:JSON.stringify({})})}finally{lastCredential=null;invalidateAuth();emit()}},
 async requestPasswordReset(email:string){return request('/request-password-reset',{method:'POST',body:JSON.stringify({email:email.trim().toLowerCase(),redirectTo:window.location.origin+'/reset-password'})})},
 async resetPassword(token:string,newPassword:string){const data=await request('/reset-password',{method:'POST',body:JSON.stringify({token,newPassword})});invalidateAuth();emit();return data},
 async changePassword(newPassword:string){if(!lastCredential)throw new Error('Por segurança, entre novamente com a senha provisória antes de criar a nova senha.');const data=await request('/change-password',{method:'POST',body:JSON.stringify({currentPassword:lastCredential,newPassword,revokeOtherSessions:true})});lastCredential=newPassword;invalidateAuth();emit();return data},
 subscribe(callback:()=>void){const listener=()=>callback();window.addEventListener(AUTH_EVENT,listener);return()=>window.removeEventListener(AUTH_EVENT,listener)}
}

